import React from 'react'
import {
  MessageSquare,
  Film,
  Upload,
  BookOpen,
  FileText,
  Code,
  HelpCircle,
  Users,
  LifeBuoy,
  Activity,
  Bot,
  Zap,
  TrendingUp,
  LayoutGrid,
  Sparkles,
  LayoutDashboard,
  PlusCircle,
  ImageIcon,
  Video,
  Music,
  CuboidIcon as Cube,
} from 'lucide-react'

/* eslint-disable quotes */
export const videoFileExts = ['mp4', 'mov', 'avi']

export const videoExtensions = ['mp4', 'mov', 'wmv', 'flv', 'avi', 'mkv', 'webm', 'm4v', '3gp', 'mpeg', 'mpg', 'ogv']

export const audioExtensions = ['mp3', 'wav', 'aac', 'flac', 'ogg', 'wma', 'm4a', 'aiff', 'alac', 'opus', 'amr']

export const gifExtensions = ['gif', 'webp']

export const image3DExtensions = ['glb', 'blend', 'fbx', 'obj', 'mtl']

export const imageExtensions = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'tiff',
  'tif',
  'webp',
  'svg',
  'heic',
  'ico',
  'raw',
  'arw',
  'cr2',
  'nef',
  'orf',
  'sr2',
]

export const mainThemes = [
  {
    label: 'Normal',
    value: '',
  },
  {
    label: 'Sharp',
    value: 'sharp-theme',
  },
  {
    label: 'Fully Sharp',
    value: 'fully-sharp-theme',
  },
]
export const chatThemes = [
  { label: 'White', value: 'theme5' },
  { label: 'Black', value: 'theme4' },
  { label: 'Environment', value: 'theme2' },
  { label: 'Matrix', value: 'theme3' },
  { label: 'Spider', value: 'theme1' },
]

export const beforeAfterModels = [
  'background-x',
  'background-replacer',
  'remove-background-c',
  'background-remover2',
  'background-x3-pro',
  'face-enhancer',
  'background-remover-pro',
]

export const aiBtnList = {
  'styley-text-to-image': { label: 'Text to Image' },
  'text-to-video': { label: 'Text to Video' },
  'text-to-3d': { label: 'Text to 3D Model' },
  'text-to-speech': { label: 'Text to Audio' },
  gpt4o: { label: 'Chat GPT', ai: true },
  claude: { label: 'Claude', ai: true },
}

export const INPUT_OPTIONS = [
  {
    value: 'image',
    label: 'Text to Image',
    icon: <ImageIcon className="h-4 w-4 mr-2" />,
    inputType: 'styley-text-to-image',
  },
  { value: 'video', label: 'Text to Video', icon: <Video className="h-4 w-4 mr-2" />, inputType: 'text-to-video' },
  { value: '3d', label: 'Text to 3D Model', icon: <Cube className="h-4 w-4 mr-2" />, inputType: 'text-to-3d' },
  { value: 'audio', label: 'Text to Audio', icon: <Music className="h-4 w-4 mr-2" />, inputType: 'text-to-audio' },
  { value: 'chatgpt', label: 'Chat GPT', icon: <MessageSquare className="h-4 w-4 mr-2" />, inputType: 'chatgpt' },
  { value: 'claude', label: 'Claude', icon: <MessageSquare className="h-4 w-4 mr-2" />, inputType: 'claude' },
]
export const defaultSelectedModel = 'styley-text-to-image'

export const filterMediaTabs = [
  'Trending',
  'My Results',
  'My Uploads',
  'Unsplash',
  'Colorful',
  'Face',
  'Clothing',
  'Model',
  'Text',
  'Background',
  'Object',
  'Lexica',
]

export const sideMenuPages = ['/dashboard', '/media', '/favorites', '/api-keys', '/billing']

export const noFooterPages = ['/create', '/chat', '/share']

export const headerFilters = [
  'All AIs',
  'Top AIs',
  'Real Estate',
  'Fashion',
  'Video',
  'Image',
  'Photo',
  'Audio',
  'Music',
  'Beauty',
  'Fun',
  'Ecom',
  'Avatar',
  '3D',
]

export const PUBLIC_ACCESS = {
  'x-mediamagic-key': '6ac59ff5-d637-11ef-8ac3-30d042e69440',
}

export const langsList = ['Python', 'Javascript', 'Go', 'PHP', 'cURL']
export const langsLinks = [
  'https://pypi.org/project/styleysdk/',
  'https://www.npmjs.com/package/@styley/typescript-sdk',
  'https://pkg.go.dev/git.mediamagic.ai/styley-sdks/styley-golang-sdk@v0.0.2',
  'https://packagist.org/packages/pingpong/modules',
  '#',
]

const runObject = (object, type, copy) => {
  return !copy
    ? `<span>${Object.keys(object).map(
        (item) =>
          `\n${type === 'PHP' ? '' : '     '}<span style="color: #D92D20">"${item}"</span>${
            type === 'PHP' ? ' =>' : ':'
          } <span  class="${
            object[item].changing ? 'rounded-md recently-edited' : ''
          }" style="color: #027A48">"${object[item].value}"</span>`
      )}</span>,`
    : `${Object.keys(object).map(
        (item) => `\n${type === 'PHP' ? '\t' : '\t\t'}"${item}"${type === 'PHP' ? ' =>' : ':'} "${object[item].value}"`
      )},`
}

export const codeSnippet = (model, t) => ({
  Python: [
    {
      header: t('model.code.python'),
      content: "pip install <span style='color: #D92D20'>styleysdk</span>",
      copyContent: 'pip install styleysdk',
    },
    {
      header: t('model.code.use-key'),
      content: 'export X_STYLEY_KEY=<api-key>\n\n\n',
      copyContent: 'export X_STYLEY_KEY=<api-key>',
    },
    {
      header: t('model.code.run-lines'),
      content: `<span>from <span style="color: #D92D20">styleysdk </span>import Styley\nfrom <span style="color: #D92D20">styleysdk.deployments.model </span>import CreateDeployment\nfrom <span style="color: #D92D20">styleysdk.deployments.model </span>import Job\n\nstyley = Styley()\ndeployment = styley.deployments.create(deployment=CreateDeployment(<span class="double-tab">name=<span style="color: #027A48">"${
        model.name
      }"</span>,</span><span class="double-tab">model=<span style="color: #027A48">"${
        model.model_identifier || model.id
      }",</span>\n<span>args={${runObject(model.args, 'Python')}\n}</span></span>))\nprint(deployment)`,
      copyContent: `from styleysdk import Styley\nfrom styleysdk.deployments.model import CreateDeployment\n\nstyley = Styley()\ndeployment = styley.deployments.create(deployment=CreateDeployment(\n\t\tname="${
        model.name
      }",\n\t\tmodel="${
        model.model_identifier || model.id
      }",\n\t\targs={${runObject(model.args, 'Python', true)}\n\t\t}\n))\nprint(deployment)`,
    },
  ],
  Javascript: [
    {
      header: t('model.code.nodejs'),
      content: "npm i --save <span style='color: #D92D20'>@styley/typescript-sdk</span>",
      copyContent: 'npm i --save @styley/typescript-sdk',
    },
    {
      header: t('model.code.use-key'),
      content: 'export X_STYLEY_KEY=<api-key>\n\n',
      copyContent: 'export X_STYLEY_KEY=<api-key>',
    },
    {
      header: t('model.code.run-lines'),
      content: `<span>import <span style="color: #D92D20">Styley </span>from <span style="color: #D92D20">'@styley/typescript-sdk'</span>;\n\n<span style="color: #D92D20">const </span>styley = <span style="color: #D92D20">new </span>Styley();\n<span style="color: #D92D20">const </span>deployment = styley.deployments.create({<span class="tab">name: <span style="color: #027A48">"${
        model.name
      }"</span>,</span><span class="tab">model: <span style="color: #027A48">"${
        model.model_identifier || model.id
      }"</span>,</span><span class="tab">args: {${runObject(model.args, 'Javascript')}\n},\nsync:false</span>});\n\nconsole.<span style="color: #D92D20">log</span>("deployment: ", deployment); </span>`,
      copyContent: `import Styley from '@styley/typescript-sdk';\n\nconst styley = new Styley();\nconst deployment = styley.deployments.create({\n\t\tname: "${
        model.name
      }",\n\t\tmodel: "${model.model_identifier || model.id}",\n\t\targs: {${runObject(
        model.args,
        'Javascript',
        true
      )}\n\t\t},\n\t\tsync:false\n\t});\nconsole.log("deployment: ", deployment);`,
    },
  ],
  Go: [
    {
      header: t('model.code.run-model'),
      content: `<span>package main\n\nimport (\n<span style="color: #D92D20" class="tab">"fmt"</span><span style="color: #D92D20" class="tab">"git.mediamagic.ai/styley-sdks/styley-golang-sdk/sdk"\n"git.mediamagic.ai/styley-sdks/styley-golang-sdk/sdk/deployments"\n</span>)\n\nfunc main() {<span class="tab">client := sdk.NewClient()\n\ndeployment, err := client.Deployments().Create(<span style="color: #D92D20">deployments.CreateDeployment</span>{\n<span class="tab">Name: <span style="color: #027A48">"${
        model.name
      }"</span>,\nArgs: map[string]interface{}{${runObject(
        model.args,
        'Go'
      )}\n},\nModel: <span style="color: #027A48">"${model.model_identifier || model.id}"</span>,\n</span>})\n\nif err != nil {\n<span class="tab">fmt.<span style="color: #D92D20">Println</span>("Error creating deployment:", err)\nreturn</span>}\n\nfmt.<span style="color: #D92D20">Printf</span>("Deployment created successfully: %+v \\n", deployment)</span>}`,
      copyContent: `package main\n\nimport (\n\t"fmt"\n\t"git.mediamagic.ai/styley-sdks/styley-golang-sdk/sdkk"\n\t"git.mediamagic.ai/styley-sdks/styley-golang-sdk/sdk/deployments"\n)\n\nfunc main() {\n\tclient := sdk.NewClient()\n\n\tdeployment, err := client.Deployments().Create(deployments.CreateDeployment{\n\t\tName: "${
        model.name
      }",\n\t\tArgs: map[string]interface{}{${runObject(
        model.args,
        'Go',
        true
      )}\n\t\t},\n\t\tModel: "${model.model_identifier || model.id}",\n\t})\n\n\tif err != nil {\n\t\tfmt.Println("Error creating deployment:", err)\n\t\treturn\n\t}\n\n\tfmt.Printf("Deployment created successfully: %+v \\n", deployment)\n}`,
    },
  ],
  cURL: [
    {
      header: t('model.code.use-key'),
      content: 'export X_STYLEY_KEY=<api-key>\n\n',
      copyContent: 'export X_STYLEY_KEY=<api-key>',
    },
    {
      header: t('model.code.run-model'),
      content: `curl --location 'https://api-qa.mediamagic.ai/api/v1/deployments'\\\n\t-H 'x-mediamagic-key: '$X_STYLEY_KEY\\\n\t-H 'content-type: application/json'\\\n\t--data-raw '{\n\t\t"name":"${
        model.name
      }",\n\t\t"model_id":"${model.model_identifier || model.id}",\n\t\t"args":{\t\t\t${runObject(
        model.args,
        'cURL',
        true
      )}\n\t\t}\n\t}'`,
      copyContent: `curl --location 'https://api-qa.mediamagic.ai/api/v1/deployments'\\\n\t-H 'x-mediamagic-key: '$X_STYLEY_KEY\\\n\t-H 'content-type: application/json'\\\n\t--data-raw '{\n\t\t"name":"${
        model.name
      }",\n\t\t"model_id":"${model.model_identifier || model.id}",\n\t\t"args":{\t\t\t${runObject(
        model.args,
        'cURL',
        true
      ).slice(0, -1)}\n\t\t}\n\t}'`,
    },
    {
      header: t('model.code.replace-job-id'),
      content: `curl --location 'https://api-qa.mediamagic.ai/api/v1/jobs/{job_id}'\\\n\t-H 'x-mediamagic-key: '$X_STYLEY_KEY`,
      copyContent: `curl --location 'https://api-qa.mediamagic.ai/api/v1/jobs/{job_id}'\\\n\t-H 'x-mediamagic-key: '$X_STYLEY_KEY'`,
    },
  ],
  PHP: [
    {
      header: t('model.code.php'),
      content: "php pyrus.phar install -o <span style='color: #D92D20'>Styley</span>",
      copyContent: 'php pyrus.phar install -o Styley',
    },
    {
      header: t('model.code.use-key'),
      content: 'export X_STYLEY_KEY=<api-key>\n\n',
      copyContent: 'export X_STYLEY_KEY=<api-key>',
    },
    {
      header: t('model.code.run-lines'),
      content: `<span>use <span style="color: #D92D20">\\Styley\\Client;</span>\n\n$client<span style="color: #D92D20"> = new </span>Client();\n<span style="color: #D92D20">$deployment </span> = $client->deployments->create(new \\Styley\\Deployments\\DeploymentInput(<span class="tab"><span style="color: #027A48">"${
        model.name
      }"</span>,</span><span class="tab"><span style="color: #027A48">"${
        model.model_identifier || model.id
      }"</span>,${runObject(model.args, 'PHP')}\n</span>));`,
      copyContent: `use \\Styley\\Client;\n\n$client = new Client();\n$deployment = $client->deployments->create(new \\Styley\\Deployments\\DeploymentInput(\n\t"${
        model.name
      }",\n\t"${model.model_identifier || model.id}",${runObject(model.args, 'PHP', true)}\n\t));`,
    },
  ],
})

export const pages = [
  {
    name: 'explore',
    link: '/explore-models',
  },
  {
    name: 'pricing',
    link: '/pricing',
  },
  {
    name: 'GPT',
    link: '/chat',
  },
  {
    name: 'dashboard',
    link: '/dashboard',
    onlyMob: true,
    authenticated: true,
  },
  {
    name: 'privacy',
    link: '/privacy',
    onlyMob: true,
  },
]

export const navbarMenuItems = [
  {
    label: 'Explore',
    href: '/explore',
    cardWidth: '800px',
    sections: [
      {
        title: 'Explore AI',
        links: [
          {
            label: 'Trending AI',
            href: '/explore/ai/trending',
            icon: Zap,
            description: 'Discover popular AI models and tools',
          },
          {
            label: 'Categories',
            href: '/explore/ai/categories',
            icon: LayoutGrid,
            description: 'Browse AI by type and capability',
          },
        ],
      },
      {
        title: 'Explore Apps',
        links: [
          {
            label: 'Popular Apps',
            href: '/explore/apps/popular',
            icon: TrendingUp,
            description: 'Most used applications and tools',
          },
          {
            label: 'New Releases',
            href: '/explore/apps/new-releases',
            icon: Activity,
            description: 'Latest applications and features',
          },
        ],
        featured: [
          {
            title: 'Featured App',
            description: 'Check out our most popular AI tool',
            icon: Zap,
            iconColor: 'text-purple-500',
            href: '/explore/featured',
            actionText: 'Try it now',
          },
        ],
      },
      {
        title: 'Agents',
        links: [
          {
            label: 'Agents Directory',
            href: '/agents',
            icon: Zap,
            description: 'Explore our directory of agents',
          },
          {
            label: 'Popular MCPs',
            href: '/agents/popular',
            icon: LayoutGrid,
            description: 'Most popular MCP servers',
          },
          {
            label: 'Create Agent',
            href: '/agents/create',
            icon: Bot,
            description: 'Build your own custom AI agent',
          },
        ],
        featured: [
          {
            title: 'New Agent Builder',
            description: 'Try our new visual agent builder',
            icon: Zap,
            iconColor: 'text-green-500',
            href: '/agents/builder',
            actionText: 'Start building',
          },
        ],
      },
    ],
    footer: {
      text: 'Looking for something specific?',
      linkText: 'Use advanced search',
      linkHref: '/explore/search',
      viewAllText: 'View all',
      viewAllHref: '/explore',
    },
  },
  {
    label: 'Help',
    href: '/help',
    cardWidth: '800px',
    sections: [
      {
        title: 'Resources',
        links: [
          {
            label: 'Documentation',
            href: '/help/documentation',
            icon: BookOpen,
            description: 'Comprehensive guides and references',
          },
          {
            label: 'Tutorials',
            href: '/help/tutorials',
            icon: FileText,
            description: 'Step-by-step learning materials',
          },
          {
            label: 'API Reference',
            href: '/help/api',
            icon: Code,
            description: 'Technical API documentation',
          },
        ],
      },
      {
        title: 'Support',
        links: [
          {
            label: 'FAQ',
            href: '/help/faq',
            icon: HelpCircle,
            description: 'Frequently asked questions',
          },
          {
            label: 'Community Forum',
            href: '/help/community',
            icon: Users,
            description: 'Connect with other users',
          },
          {
            label: 'Contact Support',
            href: '/help/contact',
            icon: LifeBuoy,
            description: 'Get help from our team',
          },
        ],
      },
      {
        title: 'Featured',
        background: 'bg-gray-50',
        links: [],
        featured: [
          {
            title: 'Getting Started Guide',
            description: 'Everything you need to know to get up and running quickly.',
            icon: Zap,
            iconColor: 'text-yellow-500',
            href: '/help/getting-started',
            actionText: 'Learn more',
          },
          {
            title: 'AI Prompt Tips',
            description: 'Learn how to craft effective prompts for better results.',
            icon: Activity,
            iconColor: 'text-purple-500',
            href: '/help/ai-tips',
            actionText: 'View tips',
          },
        ],
      },
    ],
    footer: {
      text: 'Need specialized help?',
      linkText: 'Contact our enterprise team',
      linkHref: '/help/enterprise',
      viewAllText: 'View all help resources',
      viewAllHref: '/help',
    },
  },
  {
    label: 'Pricing',
    href: '/pricing',
    cardWidth: '600px',
  },
]

export const chatOption = [
  { value: 'groq', label: 'Groq' },
  { value: 'gpt4o', label: 'GPT4O' },
  { value: 'claude', label: 'Claude 3' },
]

export const privatePages = ['/models', '/account', '/api-keys', '/automation', '/billing', '/dashboard']

export const deploymentStatuses = {
  pending: 'Job is inqueue, waiting for a gpu to be assigned for inference.',
  'docker-pulling': 'Docker Image is getting pulled for docker start.',
  starting: 'Health check for the container (model) is in progress.',
  running: 'Job is running.',
  complete: 'Deployment Completed Successfully!',
}

export const waitingStatuses = [
  'waiting',
  'pending',
  'starting',
  'running',
  'docker-pulling',
  'docker_pulling',
  'training',
]

export const runningStatuses = ['running', 'complete', 'failed']

export const sideNavePopupValue = [
  { id: '13', image: '📨', name: 'Cold Email' },
  { id: '14', image: '✍️', name: 'Copy Writing' },
  { id: '15', image: '🧑‍💻', name: 'Coding' },
  { id: '137 ', image: '📝', name: 'Influencer Marketing' },
  { id: '16', image: '📝', name: 'Education' },
  { id: '17', image: '💥', name: 'Cooking' },
  { id: '18', image: '🧠', name: 'Mental Models' },
  { id: '126', image: '💡', name: 'Instagram Stories' },
  { id: '125', image: '💶', name: 'Twitter Stories' },
  { id: '127', image: '💡', name: 'Facebook Ad Copy' },
  { id: '19', image: '🎙️', name: 'Music' },
  { id: '110', image: '🧑‍💻', name: 'Blog Writing' },
  { id: '20', image: '🧠', name: 'Psychology Frameworks' },
  { id: '124', image: '🎥', name: 'Content Creation' },
  { id: '128', image: '💶', name: 'Youtube Ad Scripts' },
  { id: '21', image: '📈', name: 'Growth Hacking' },
  { id: '22', image: '🧠', name: 'Health' },
  { id: '23', image: '🥳', name: 'Fun' },
  { id: '25', image: '⚙️', name: 'General ChatGPT Tips' },
  { id: '133', image: '🧑‍💻', name: 'Building Linkedin Hashtags' },
  { id: '53', image: '💶', name: 'B2B Linkedin Growth Automation' },
  { id: '49', image: '📈', name: 'Automating Linkedin Content Growth' },
  { id: '132', image: '🌐', name: 'Analyzing URL links' },
  { id: '111', image: '🛸', name: 'Onboarding Messages' },
  { id: '89', image: '👤', name: 'Creating Buyer Personal' },
  { id: '199', image: '💡', name: 'Generating Ideas For Customer Surveys' },
  { id: '236', image: '📈', name: 'Writing A Proposal For A Potential Client' },
  { id: '107', image: '🥶', name: 'Writing Sales Copy for Cold Calling' },
  { id: '100', image: '🚩', name: 'Writing Linkedin Posts' },
  { id: '67', image: '💡', name: 'Generating Linkedin Content Ideas' },
  { id: '26', image: '💶', name: 'Identifying Trending Topics' },
  { id: '149', image: '📈', name: 'Writing Performance Reports' },
  { id: '138', image: '📝', name: 'Generating Influencer Agreements' },
  { id: '47', image: '👁️', name: 'Automating Influencer Outreach' },
  { id: '99', image: '✍️', name: 'Writing Creative Briefs For Influencer' },
  { id: '136', image: '💬', name: 'Personalizing Messages For Influencers' },
  { id: '103', image: '🧑‍💻', name: 'Writing Pitch For Influencer' },
  { id: '59', image: '🧠', name: 'Brainstorming Unique Marketing Angles' },
  { id: '92', image: '🧑‍💻', name: 'Research Pains and Desires Of A Buyer Persona In Minutes' },
  { id: '65', image: '🌄', name: 'Generating Images That Grab Attention' },
  { id: '113', image: '🎥', name: 'Writing A Brief For Video Editor' },
  { image: '💶', name: 'Writing Ad Copy That Converts' },
  { image: '✍️', name: 'Rewriting A/B Version Of Winning Creative' },
  { image: '🤟', name: 'Coming Up With Dozens Of Cool Captions' },
  { image: '🐒', name: 'Generating Ideas For Live Streaming' },
  { image: '💥', name: 'Automating Tiktok Channel Growth' },
  { image: '📌', name: 'Generating Pinterest Board Ideas' },
  { image: '😤', name: 'Generating Engaging Content Ideas' },
  { image: '📺', name: 'Automating Youtube Channel Growth' },
  { image: '🎙️', name: 'Creating Engaging and Informative Podcasts' },
  { image: '📈', name: 'Automating Instagram Growth' },
  { image: '💬', name: 'Writing Medium-Form Sales Copy' },
  { image: '🤑', name: 'Making Ad Copy More Interesting' },
  { image: '📙', name: 'Writing Benefit-Driven Sales Copy' },
  { image: '✍️', name: 'Writing Long-Form Sales Copy' },
  { image: '✨', name: "Crafting Unique USP's" },
  { image: '💡', name: 'Getting Ideas For Headlines' },
  { image: '🧑‍💻', name: 'Translating Ad Copy' },
  { image: '📝', name: 'Creating TikTok Content Ideas' },
  { image: '💰', name: 'Generating Creative Ideas For TikTok Trends' },
  { image: '🧨', name: 'Writing Viral Tiktok Scripts' },
  { image: '🧲', name: 'Optimizing CTA' },
  { image: '🖼️', name: 'Creating Visual Ideas For Website' },
  { image: '📨', name: 'Crafting Irresistible Offers' },
  { image: '💻', name: 'Writing Product Descriptions' },
  { image: '🍚', name: 'Translating Website Copy' },
  { image: '🎭', name: 'Implementing Cross-selling Strategies' },
  { image: '💼', name: 'Creating Product Bundling Strategies' },
  { image: '📎', name: 'Writing Product Testimonials' },
  { image: '👆', name: 'Implementing Upselling Strategies' },
  { image: '📝', name: 'Writing Product Reviews' },
  { image: '💥', name: 'Generating Landing Page Headlines' },
  { image: '⏰', name: 'Writing 100 Product Descriptions In Seconds' },
  { image: '🎭', name: 'Generating Product Comparison Infographics' },
  { image: '📝', name: 'Finding Product Benefits For Landing Page' },
  { image: '💬', name: 'Responding To Comments With AI' },
  { image: '✍️', name: 'Creating FAQs for Customers' },
  { image: '↩️', name: 'Summarizing Customer Feedback' },
  { image: '🥳', name: 'B2B Twitter Growth Automation' },
  { image: '🐤', name: 'Going Viral On Twitter' },
  { image: '📈', name: 'Automating Twitter Growth' },
  { image: '🐣', name: 'Writing Twitter Content' },
  { image: '🛬', name: 'Inbounding Lead Generation on Twitter' },
  { image: '🚀', name: '10 Ways To Get Massively Ahead With AI' },
  { image: '✎', name: 'Snippets to Improve ChatGPT Writing Style' },
  { image: '🌐', name: 'Analyzing URL links' },
  { image: '👨‍🎨', name: 'The Variety of "Act as.." Personas' },
  { image: '👏', name: 'How ChatGPT Actually Works' },
  { image: '👨‍💻', name: 'Becoming An Expert In ChatGPT' },
  { image: '🗞️', name: 'Offer Letter Automation' },
  { image: '👫🏻', name: 'Developing Customer Retention Strategies' },
  { image: '💍', name: 'Engage New Hires' },
  { image: '🪀', name: 'Product Launching' },
  { image: '🌌', name: 'Crafting Unique Product Bundles' },
  { image: '💵', name: 'Business Ideas Without Funding' },
  { image: '📒', name: 'Summarizing Meeting Notes' },
  { image: '📔', name: 'Generating long-tail keywords' },
  { image: '🔨', name: 'Developing a Brand Voice and Tone Guide' },
  { image: '🤖', name: 'AI Assistant For Spreadsheets' },
  { image: '🛄', name: 'Automatization of Hiring' },
  { image: '💥', name: 'Analyzing Your Competition' },
  { image: '📈', name: 'Crafting Goals, OKRs Based On Your Goal' },
  { image: '📕', name: 'Writing Email Subject Lines' },
  { image: '💰', name: 'Writing Sales Emails' },
  { image: '💬', name: 'SMS Marketing' },
  { image: '✉️', name: 'Writing Email Newsletters' },
  { image: '📩', name: 'Writing Product Launch Emails' },
  { image: '💶', name: 'Building Email Automations' },
  { image: '📧', name: 'Generating Cold Email Copy' },
  { image: '💡', name: 'Generating Content Ideas' },
  { image: '💬', name: '25 Templates For SEO Optimization' },
  { image: '🚩', name: 'Writing Blog Post' },
  { image: '🎥', name: 'Writing Video Descriptions' },
  { image: '📈', name: 'Automating Growth on YouTube' },
  { image: '⌨️', name: 'Creating YouTube Video Scripts' },
  { image: '🌟', name: 'Writing Twitter Threads' },
  { image: '🔝', name: 'Twitter Profile Optimization' },
  { image: '📥', name: 'Responding To Customer Emails' },
]

// Add new configuration for header buttons and responsive UI
export const headerButtonConfig = {
  // Buttons with their priorities (higher number = higher priority)
  buttons: [
    {
      icon: LayoutDashboard,
      href: '/dashboard',
      variant: 'ghost',
      className: 'px-3',
    },
    {
      icon: MessageSquare,
      href: '/chat',
      variant: 'ghost',
      className: 'px-3',
    },
    {
      icon: Upload,
      action: 'upload',
      variant: 'ghost',
      className: 'px-3',
    },
    {
      label: 'Create',
      icon: Film,
      action: 'editor',
      variant: 'ghost',
      className: 'px-3',
    },
    {
      label: 'Create 2',
      icon: Sparkles,
      href: '/create',
      variant: 'gradient',
    },
  ],
}
